/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {CookieConsentModule, OnCookieConsentChanged} from "@modules/CookieConsentModule";
import {COLORS} from "@res/colors";

const PRIVACY_POLICY_URL = "https://elliq.com/pages/elliq-privacy-policy";

export class CookieBanner
    extends BaseComponent<{}, {}>
    implements OnCookieConsentChanged {

    __onCookieConsentChanged = () => this.forceUpdate();

    private onAccept = () => CookieConsentModule.accept();
    private onDecline = () => CookieConsentModule.decline();

    render() {
        if (!CookieConsentModule.needsDecision())
            return null;

        return (
            <div
                role="dialog"
                aria-live="polite"
                aria-label="Cookie consent"
                style={styles.container}
            >
                <div style={styles.text}>
                    We use cookies that are strictly necessary to run the Care Center, and optional cookies
                    to help us understand usage and improve the product. You can accept all cookies or
                    decline the optional ones. See our{" "}
                    <a href={PRIVACY_POLICY_URL} style={styles.link} target="_blank" rel="noreferrer">
                        Privacy Policy
                    </a>{" "}
                    for details.
                </div>
                <div style={styles.actions}>
                    <button type="button" onClick={this.onDecline} style={styles.secondaryButton}>
                        Decline
                    </button>
                    <button type="button" onClick={this.onAccept} style={styles.primaryButton}>
                        Accept
                    </button>
                </div>
            </div>
        );
    }
}

const styles: {[k: string]: React.CSSProperties} = {
    container: {
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "16px 20px",
        background: COLORS.white,
        color: COLORS.neutral_text_dark,
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 8,
        boxShadow: `0 4px 16px ${COLORS.box_shadow}`,
        fontSize: 14,
        lineHeight: 1.5,
    },
    text: {
        flex: "1 1 320px",
        minWidth: 240,
    },
    link: {
        color: COLORS.primary_dark_blue,
        textDecoration: "underline",
    },
    actions: {
        display: "flex",
        gap: 8,
        flexShrink: 0,
    },
    primaryButton: {
        padding: "8px 16px",
        minWidth: 96,
        background: COLORS.primary_blue,
        color: COLORS.white,
        border: `1px solid ${COLORS.primary_blue}`,
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 600,
    },
    secondaryButton: {
        padding: "8px 16px",
        minWidth: 96,
        background: COLORS.neutral_graybluish,
        color: COLORS.neutral_text_dark,
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 600,
    },
};
